import { createApi } from "@reduxjs/toolkit/dist/query/react"
import { apiQuery } from "../../utils/api-config";
import { ResponseDepartment, ResponseDepartments } from "../../types/department/ResponseDepartments";
import { ModificationDTO } from "../../types/init/modificationDTO";
import { GetIikoDepartmentsRequest, GetIspringDepartmentsRequest, GetZupDepartmentsRequest, IikoDepartmentsResponse, ISpringDepartmentsResponse, SearchDepartmentsRequest, SearchDepartmentsResponse, ZupDepartmentsResponse } from "../../features/integration-setting-table/DepartmentsTable/department.type";

export const departmentApi = createApi({
    reducerPath: "department",
    baseQuery: apiQuery,
    tagTypes: ["departments", "departmentsSearch", "unmappedDepartments"],
    endpoints: builder => ({
        getDepartments: builder.query<ResponseDepartments, void>({
            query: () => '/departments',
            providesTags: ["departments"]
        }),
        getZupDepartments: builder.query<ZupDepartmentsResponse, GetZupDepartmentsRequest>({
            query: (params) => ({
                url: '/1c/departments',
                method: 'GET',
                params
            }),
            providesTags: ["departments"]
        }),
        getZupUnmappedDepartments: builder.query<ZupDepartmentsResponse, GetZupDepartmentsRequest>({
            query: (params) => ({
                url: '/1c/unmapped/departments',
                method: 'GET',
                params
            }),
            providesTags: ["unmappedDepartments"]
        }),
        getIikoDepartments: builder.query<IikoDepartmentsResponse, GetIikoDepartmentsRequest>({
            query: (params) => ({
                url: '/iiko/departments',
                method: 'GET',
                params
            }),
            providesTags: ["departments"]
        }),
        getIspringDepartments: builder.query<ISpringDepartmentsResponse, GetIspringDepartmentsRequest>({
            query: (params) => ({
                url: '/ispring/departments',
                method: 'GET',
                params
            }),
            providesTags: ["departments"]
        }),
        searchDepartments: builder.query<SearchDepartmentsResponse, SearchDepartmentsRequest>({
            query: (data) => ({
                url: '/departments/search',
                method: 'POST',
                body: data
            }),
            providesTags: ["departmentsSearch"]
        }),
        createDepartment: builder.mutation<ResponseDepartment, Partial<ModificationDTO>>({
            query: (data) => ({
                url: '/departments',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['departmentsSearch', "unmappedDepartments"]
        }),
        updateDepartment: builder.mutation<ResponseDepartment, Partial<{ id: number, data: ModificationDTO }>>({
            query: ({ id, data }) => ({
                url: `/departments/${id}`,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ['departmentsSearch', "unmappedDepartments"]
        }),
        removeDepartments: builder.mutation<void, Partial<number>>({
            query: id => ({
                url: `/departments/${id}`,
                method: "DELETE"
            }),
            invalidatesTags: ["departmentsSearch", "unmappedDepartments"]
        }),
        updateIikoDepartment: builder.mutation<void, string>({
            query: (code) => ({
                url: `/iiko/department/${code}`,
                method: "PATCH"
            }),
            invalidatesTags: ["departments", "departmentsSearch"]
        }),
        updateZupDepartment: builder.mutation<void, string>({
            query: (id) => ({
                url: `/1c/department/${id}`,
                method: "PATCH"
            }),
            invalidatesTags: ["departments", "departmentsSearch"]
        }),
        updateIspringDepartment: builder.mutation<void, string>({
            query: (id) => ({
                url: `/ispring/department/${id}`,
                method: "PATCH"
            }),
            invalidatesTags: ["departments", "departmentsSearch"]
        }),
        removeIikoDepartment: builder.mutation<void, string>({
            query: (code) => ({
                url: `/iiko/department/${code}`,
                method: "DELETE"
            }),
            invalidatesTags: ["departments", "departmentsSearch"]
        }),
        removeZupDepartment: builder.mutation<void, string>({
            query: (id) => ({
                url: `/1c/department/${id}`,
                method: "DELETE"
            }),
            invalidatesTags: ["departments", "departmentsSearch"]
        }),
        removeIspringDepartment: builder.mutation<void, string>({
            query: (id) => ({
                url: `/ispring/department/${id}`,
                method: "DELETE"
            }),
            invalidatesTags: ["departments", "departmentsSearch"]
        })
    })
})

export const {
    useGetDepartmentsQuery,
    useGetIikoDepartmentsQuery,
    useLazyGetIikoDepartmentsQuery,
    useGetIspringDepartmentsQuery,
    useSearchDepartmentsQuery,
    useCreateDepartmentMutation,
    useUpdateDepartmentMutation,
    useRemoveDepartmentsMutation,
    useGetZupDepartmentsQuery,
    useLazyGetZupDepartmentsQuery,
    useUpdateIikoDepartmentMutation,
    useUpdateZupDepartmentMutation,
    useUpdateIspringDepartmentMutation,
    useRemoveIikoDepartmentMutation,
    useRemoveZupDepartmentMutation,
    useRemoveIspringDepartmentMutation,
    useGetZupUnmappedDepartmentsQuery
} = departmentApi
